import { useEffect, useState, createContext, useContext, useCallback, ReactNode } from "react";

interface LiveRegionContextValue {
  announce: (message: string, priority?: "polite" | "assertive") => void;
}

const LiveRegionContext = createContext<LiveRegionContextValue | null>(null);

/**
 * Provider for screen reader announcements.
 * Wrap your app with this to enable live announcements.
 */
export function LiveRegionProvider({ children }: { children: ReactNode }) {
  const [politeMessage, setPoliteMessage] = useState("");
  const [assertiveMessage, setAssertiveMessage] = useState("");

  const announce = useCallback((message: string, priority: "polite" | "assertive" = "polite") => {
    if (priority === "assertive") {
      setAssertiveMessage("");
      // Force re-render by clearing then setting
      requestAnimationFrame(() => setAssertiveMessage(message));
    } else {
      setPoliteMessage("");
      requestAnimationFrame(() => setPoliteMessage(message));
    }
  }, []);

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      {children}
      
      {/* Polite announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage}
      </div>
      
      {/* Assertive announcements (interrupts) */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage}
      </div>
    </LiveRegionContext.Provider>
  );
}

/**
 * Hook to announce messages to screen readers.
 * 
 * Usage:
 * const { announce } = useLiveRegion();
 * announce("Task completed successfully");
 * announce("Error: Failed to save", "assertive");
 */
export function useLiveRegion() {
  const context = useContext(LiveRegionContext);
  
  if (!context) {
    // Return a no-op if provider not found (graceful degradation)
    return {
      announce: (_message: string, _priority?: "polite" | "assertive") => {}
    };
  }
  
  return context;
}
